'use client'

import { cn } from 'app/lib/utils'
import Image from 'next/image'
import { useEffect, useState, useCallback, useRef } from 'react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'app/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'app/components/ui/table'
import { Input } from 'app/components/ui/input'
import { Skeleton } from 'app/components/ui/skeleton'
import { IoIosArrowDown } from 'react-icons/io'
import { RiArrowRightUpFill } from 'react-icons/ri'
import { PolygonWidgetWrapper } from 'components/WidgetWrapper'
import { Seasons_getDescription } from 'utils/seasons'
import Link from 'app/components/shared/link'
import {
    fetchOrganisationCompetitions,
    fetchCompetitionSeasons,
    fetchOrgTopTeams,
    fetchOrgTopPlayers
} from './client/fetchStandingStatData'
import { persistentQuery } from 'hooks/usePersistentData'

/** Prefer competitions that already have seasons/matches over empty shells. */
function pickDefaultCompetitionId(
    comps: Array<{ id: string; seasonCount?: number; matchCount?: number }>
): string | undefined {
    if (!comps.length) return undefined
    const withActivity = comps.find(c => (c.seasonCount ?? 0) > 0 || (c.matchCount ?? 0) > 0)
    return (withActivity ?? comps[0])?.id
}

interface StandingStatWidgetProps {
    data?: IStandingStatWidget
    config?: IDomainConfig
    styles?: IWidgetStyles
    /**
     * Pre-fetched widget data from server component.
     * If provided, this widget will use it instead of making client-side API calls.
     */
    widgetData?: import('app/types/widget-data').StandingStatWidgetData | null
}

const StandingStatWidget = ({ data, config, widgetData }: StandingStatWidgetProps) => {
    const showTeamTable = data?.showTeamTable ?? true
    const showPlayerTable = data?.showPlayerTable ?? true
    const enableFilters = data?.enableFilters ?? true
    const rowCount = data?.rowCount || 5
    const orgId = config?.organisation_id
    const rootRef = useRef<HTMLDivElement>(null)
    const [shouldLoad, setShouldLoad] = useState(false)

    useEffect(() => {
        const el = rootRef.current
        if (!el || shouldLoad) return
        if (typeof IntersectionObserver === 'undefined') {
            setShouldLoad(true)
            return
        }
        const io = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setShouldLoad(true)
                    io.disconnect()
                }
            },
            { rootMargin: '240px' }
        )
        io.observe(el)
        return () => io.disconnect()
    }, [shouldLoad])

    // Determine initial active button based on which tables are enabled
    const getInitialActiveButton = (): 'first' | 'second' => {
        if (showTeamTable && !showPlayerTable) return 'first'
        if (!showTeamTable && showPlayerTable) return 'second'
        return 'first' // Both enabled, default to teams
    }

    const [activeButton, setActiveButton] = useState<'first' | 'second'>(getInitialActiveButton())
    const [teamData, setTeamData] = useState<ITopTeamStats[]>([])
    const [playerData, setPlayerData] = useState<{
        topScorers: ITopPlayerStats[]
        topAssists: ITopPlayerStats[]
        topGoalkeepers: ITopPlayerStats[]
    }>({
        topScorers: [],
        topAssists: [],
        topGoalkeepers: []
    })
    // Separate loading states for team and player to avoid cross-interference
    const [teamLoading, setTeamLoading] = useState(true)
    const [playerLoading, setPlayerLoading] = useState(true)

    // Filter states (Federation is set in admin, only Competition and Season are user-selectable)
    const [competitions, setCompetitions] = useState<ICompetition[]>([])
    const [seasons, setSeasons] = useState<ISeason[]>([])
    const [competitionsLoading, setCompetitionsLoading] = useState(false)
    const [seasonsLoading, setSeasonsLoading] = useState(false)
    const [playerSeasonsLoading, setPlayerSeasonsLoading] = useState(false)

    const [selectedCompetition, setSelectedCompetition] = useState<string | undefined>(data?.competitionId)
    const [selectedSeason, setSelectedSeason] = useState<string | undefined>(data?.seasonId)

    // Player table filter states (separate from team table - no fallback to team config)
    const [playerSeasons, setPlayerSeasons] = useState<ISeason[]>([])
    const [selectedPlayerCompetition, setSelectedPlayerCompetition] = useState<string | undefined>(
        data?.playerCompetitionId
    )
    const [selectedPlayerSeason, setSelectedPlayerSeason] = useState<string | undefined>(data?.playerSeasonId)

    // Handler for competition change - clears season if not valid for new competition
    const handleCompetitionChange = (competitionId: string) => {
        setSelectedCompetition(competitionId)
        // If competition is cleared or changed, clear season (will be re-validated in useEffect)
        if (competitionId === 'all' || !competitionId) {
            if (!data?.seasonId) {
                setSelectedSeason(undefined)
            }
        } else {
            // Clear season if it's not a default from config
            // The useEffect will validate and set it if it's valid for the new competition
            if (!data?.seasonId || data.competitionId !== competitionId) {
                setSelectedSeason(undefined)
            }
        }
    }

    const [isCompetitionSelectOpen, setIsCompetitionSelectOpen] = useState(false)
    const [isSeasonSelectOpen, setIsSeasonSelectOpen] = useState(false)
    const [competitionSearchQuery, setCompetitionSearchQuery] = useState('')

    // Player table filter UI state
    const [isPlayerCompetitionSelectOpen, setIsPlayerCompetitionSelectOpen] = useState(false)
    const [isPlayerSeasonSelectOpen, setIsPlayerSeasonSelectOpen] = useState(false)
    const [playerCompetitionSearchQuery, setPlayerCompetitionSearchQuery] = useState('')

    // Handler for player competition change
    const handlePlayerCompetitionChange = (competitionId: string) => {
        setSelectedPlayerCompetition(competitionId)
        if (competitionId === 'all' || !competitionId) {
            if (!data?.playerSeasonId) {
                setSelectedPlayerSeason(undefined)
            }
        } else {
            if (!data?.playerSeasonId || data.playerCompetitionId !== competitionId) {
                setSelectedPlayerSeason(undefined)
            }
        }
    }

    // Update selected values when config data changes (each table uses only its own config)
    // Only overwrite when config has explicit values - preserve auto-selected when config is empty
    useEffect(() => {
        if (!data) return
        if (data.competitionId !== undefined) setSelectedCompetition(data.competitionId)
        if (data.seasonId !== undefined) setSelectedSeason(data.seasonId)
        if (data.playerCompetitionId !== undefined) setSelectedPlayerCompetition(data.playerCompetitionId)
        if (data.playerSeasonId !== undefined) setSelectedPlayerSeason(data.playerSeasonId)
    }, [data?.competitionId, data?.seasonId, data?.playerCompetitionId, data?.playerSeasonId])

    // Fetch competitions on mount (Federation is set in admin)
    useEffect(() => {
        if (!shouldLoad) return
        // If pre-fetched widget data is available, use it instead of fetching
        if (widgetData?.competitions && widgetData.competitions.length > 0) {
            setCompetitions(widgetData.competitions)
            // Set default competition from config if available, otherwise auto-select first (team table)
            if (data?.competitionId && widgetData.competitions.some(c => c.id === data.competitionId)) {
                if (!selectedCompetition || selectedCompetition !== data.competitionId) {
                    setSelectedCompetition(data.competitionId)
                }
            } else if (!selectedCompetition && !data?.competitionId) {
                const defaultId = pickDefaultCompetitionId(widgetData.competitions)
                if (defaultId) setSelectedCompetition(defaultId)
            }
            // Set default player competition from config if available, otherwise auto-select first (player table)
            if (data?.playerCompetitionId && widgetData.competitions.some(c => c.id === data.playerCompetitionId)) {
                if (!selectedPlayerCompetition || selectedPlayerCompetition !== data.playerCompetitionId) {
                    setSelectedPlayerCompetition(data.playerCompetitionId)
                }
            } else if (!selectedPlayerCompetition && !data?.playerCompetitionId) {
                const defaultId = pickDefaultCompetitionId(widgetData.competitions)
                if (defaultId) setSelectedPlayerCompetition(defaultId)
            }
            return
        }

        const fetchFilters = async () => {
            if (!orgId) return

            setCompetitionsLoading(true)
            try {
                // Fetch all competitions for the organization
                const competitionsData = await persistentQuery(
                    `standing:competitions:${orgId}`,
                    () => fetchOrganisationCompetitions(orgId),
                    10 * 60 * 1000
                )
                if (competitionsData && competitionsData.length > 0) {
                    setCompetitions(competitionsData)
                    // Set default competition from config if available, otherwise auto-select first
                    if (data?.competitionId && competitionsData.some(c => c.id === data.competitionId)) {
                        // Default competition from config exists and is valid
                        if (!selectedCompetition || selectedCompetition !== data.competitionId) {
                            setSelectedCompetition(data.competitionId)
                        }
                    } else if (!selectedCompetition && !data?.competitionId) {
                        // Prefer comps with seasons/matches over empty shells
                        const defaultId = pickDefaultCompetitionId(competitionsData)
                        if (defaultId) setSelectedCompetition(defaultId)
                    }
                    // Auto-select first competition for player table when not in config
                    if (data?.playerCompetitionId && competitionsData.some(c => c.id === data.playerCompetitionId)) {
                        if (!selectedPlayerCompetition || selectedPlayerCompetition !== data.playerCompetitionId) {
                            setSelectedPlayerCompetition(data.playerCompetitionId)
                        }
                    } else if (!selectedPlayerCompetition && !data?.playerCompetitionId) {
                        const defaultId = pickDefaultCompetitionId(competitionsData)
                        if (defaultId) setSelectedPlayerCompetition(defaultId)
                    }
                }
            } catch (error) {
                console.error('Error fetching filters:', error)
            } finally {
                setCompetitionsLoading(false)
            }
        }

        fetchFilters()
    }, [shouldLoad, orgId, data?.competitionId, widgetData?.competitions])

    // Fetch seasons when a competition is selected
    useEffect(() => {
        if (!shouldLoad) return
        const fetchSeasons = async () => {
            if (!selectedCompetition || selectedCompetition === 'all') {
                setSeasons([])
                // Clear season when competition is cleared (unless it's a default from config and we're just initializing)
                if (!data?.seasonId || selectedCompetition === 'all') {
                    setSelectedSeason(undefined)
                }
                return
            }

            // If pre-fetched seasons are available and match the selected competition, use them
            if (widgetData?.seasons && widgetData.seasons.length > 0) {
                const matchingSeasons = widgetData.seasons.filter(s => s.competition_id === selectedCompetition)
                if (matchingSeasons.length > 0) {
                    setSeasons(matchingSeasons)
                    // Handle default season logic
                    const isDefaultCompetition = data?.competitionId === selectedCompetition
                    const isDefaultSeasonValid = matchingSeasons.some(s => s.id === data?.seasonId)
                    const isCurrentSeasonValid = selectedSeason && matchingSeasons.some(s => s.id === selectedSeason)

                    if (isDefaultSeasonValid && data?.seasonId && (isDefaultCompetition || !selectedSeason)) {
                        setSelectedSeason(data.seasonId)
                    } else if (!isCurrentSeasonValid && selectedSeason) {
                        setSelectedSeason(undefined)
                    }
                    return
                }
            }

            setSeasonsLoading(true)
            try {
                const seasonsData = await persistentQuery(
                    `standing:seasons:${selectedCompetition}`,
                    () => fetchCompetitionSeasons(selectedCompetition),
                    10 * 60 * 1000
                )
                if (seasonsData && seasonsData.length > 0) {
                    setSeasons(seasonsData)

                    // Check if default season from config is valid for this competition
                    const isDefaultSeasonValid = data?.seasonId && seasonsData.some(s => s.id === data.seasonId)

                    // Check if current season is valid for the new competition
                    const isCurrentSeasonValid = selectedSeason && seasonsData.some(s => s.id === selectedSeason)

                    // Check if this is the default competition from config
                    const isDefaultCompetition = data?.competitionId && selectedCompetition === data.competitionId

                    // Priority logic:
                    // 1. If this is the default competition and default season is valid, use it
                    // 2. If default season is valid (regardless of competition), use it
                    // 3. If current season is valid, keep it
                    // 4. Otherwise, clear and auto-select first season
                    if (isDefaultSeasonValid && data?.seasonId && (isDefaultCompetition || !selectedSeason)) {
                        // Set the default season from config if it's valid for this competition
                        // Prioritize when it's the default competition, or when no season is currently selected
                        setSelectedSeason(data.seasonId)
                    } else if (isCurrentSeasonValid) {
                        // Keep current season if it's valid
                        // (already set, no need to change)
                    } else {
                        // Current season is not valid, clear it
                        if (selectedSeason) {
                            setSelectedSeason(undefined)
                        }
                        // Don't auto-select first season - let user explicitly choose
                        // This prevents flickering caused by season-level queries returning empty data
                        // when competition-level data exists
                    }
                } else {
                    setSeasons([])
                    // Clear season if no seasons available (unless it's a default from config)
                    if (!data?.seasonId) {
                        setSelectedSeason(undefined)
                    }
                }
            } catch (error) {
                console.error('Error fetching seasons:', error)
                setSeasons([])
                // Clear season on error (unless it's a default from config)
                if (!data?.seasonId) {
                    setSelectedSeason(undefined)
                }
            } finally {
                setSeasonsLoading(false)
            }
        }

        fetchSeasons()
    }, [shouldLoad, selectedCompetition, data?.seasonId, data?.competitionId])

    // Fetch player seasons when player competition is selected
    useEffect(() => {
        if (!shouldLoad) return
        if (showTeamTable && showPlayerTable && activeButton !== 'second') return
        const fetchPlayerSeasons = async () => {
            if (!selectedPlayerCompetition || selectedPlayerCompetition === 'all') {
                setPlayerSeasons([])
                setPlayerSeasonsLoading(false)
                if (!data?.playerSeasonId || selectedPlayerCompetition === 'all') {
                    setSelectedPlayerSeason(undefined)
                }
                return
            }

            if (widgetData?.seasons && widgetData.seasons.length > 0) {
                const matchingSeasons = widgetData.seasons.filter(s => s.competition_id === selectedPlayerCompetition)
                if (matchingSeasons.length > 0) {
                    setPlayerSeasons(matchingSeasons)
                    const isDefaultComp = data?.playerCompetitionId === selectedPlayerCompetition
                    const isDefaultSeasonValid = matchingSeasons.some(s => s.id === data?.playerSeasonId)
                    const isCurrentValid =
                        selectedPlayerSeason && matchingSeasons.some(s => s.id === selectedPlayerSeason)

                    if (isDefaultSeasonValid && data?.playerSeasonId && (isDefaultComp || !selectedPlayerSeason)) {
                        setSelectedPlayerSeason(data.playerSeasonId)
                    } else if (!isCurrentValid && selectedPlayerSeason) {
                        setSelectedPlayerSeason(undefined)
                    }
                    return
                }
            }

            setPlayerSeasonsLoading(true)
            try {
                const seasonsData = await persistentQuery(
                    `standing:seasons:${selectedPlayerCompetition}`,
                    () => fetchCompetitionSeasons(selectedPlayerCompetition),
                    10 * 60 * 1000
                )
                if (seasonsData && seasonsData.length > 0) {
                    setPlayerSeasons(seasonsData)
                    const isDefaultSeasonValid =
                        data?.playerSeasonId && seasonsData.some(s => s.id === data.playerSeasonId)
                    const isCurrentValid = selectedPlayerSeason && seasonsData.some(s => s.id === selectedPlayerSeason)
                    const isDefaultComp = data?.playerCompetitionId === selectedPlayerCompetition

                    if (isDefaultSeasonValid && data?.playerSeasonId && (isDefaultComp || !selectedPlayerSeason)) {
                        setSelectedPlayerSeason(data.playerSeasonId)
                    } else if (!isCurrentValid && selectedPlayerSeason) {
                        setSelectedPlayerSeason(undefined)
                    }
                } else {
                    setPlayerSeasons([])
                    if (!data?.playerSeasonId) {
                        setSelectedPlayerSeason(undefined)
                    }
                }
            } catch (error) {
                console.error('Error fetching player seasons:', error)
                setPlayerSeasons([])
                if (!data?.playerSeasonId) {
                    setSelectedPlayerSeason(undefined)
                }
            } finally {
                setPlayerSeasonsLoading(false)
            }
        }

        fetchPlayerSeasons()
    }, [
        shouldLoad,
        showTeamTable,
        showPlayerTable,
        activeButton,
        selectedPlayerCompetition,
        data?.playerSeasonId,
        data?.playerCompetitionId
    ])

    // Fetch team data
    const fetchTeamData = useCallback(async () => {
        if (!orgId) {
            setTeamLoading(false)
            return
        }

        // If pre-fetched data is available and matches current filters, use it
        if (widgetData?.topTeams && !selectedCompetition && !selectedSeason && !data?.federationId) {
            setTeamData(widgetData.topTeams.slice(0, rowCount))
            setTeamLoading(false)
            return
        }

        setTeamLoading(true)
        try {
            const filters: any = {
                org_id: orgId,
                page: 1,
                page_size: rowCount,
                sort_by: data?.sortBy || 'points',
                order: 'desc'
            }

            // Apply filters in priority order: Season > Competition > Federation
            // Only use season if it's valid for the selected competition
            if (selectedSeason && selectedSeason !== 'all') {
                // Verify season is valid for current competition if competition is selected
                if (selectedCompetition && selectedCompetition !== 'all') {
                    filters.season_id = selectedSeason
                } else {
                    // No competition selected, use season filter
                    filters.season_id = selectedSeason
                }
            } else if (selectedCompetition && selectedCompetition !== 'all') {
                filters.competition_id = selectedCompetition
            } else if (data?.federationId) {
                filters.federation_id = data.federationId
            }

            const teams = await persistentQuery(
                `standing:teams:${JSON.stringify(filters)}`,
                () => fetchOrgTopTeams(filters),
                2 * 60 * 1000
            )
            if (teams) {
                setTeamData(teams.slice(0, rowCount))
            }
        } catch (error) {
            console.error('Error fetching team data:', error)
        } finally {
            setTeamLoading(false)
        }
    }, [orgId, rowCount, data?.sortBy, data?.federationId, selectedCompetition, selectedSeason, widgetData?.topTeams])

    // Fetch player data
    const fetchPlayerData = useCallback(async () => {
        if (!orgId) {
            setPlayerLoading(false)
            return
        }

        // If pre-fetched data is available and matches current filters, use it (player table uses only its own federation)
        const playerFederation = data?.playerFederationId
        if (widgetData?.topPlayers && !selectedPlayerCompetition && !selectedPlayerSeason && !playerFederation) {
            setPlayerData({
                topScorers: widgetData.topPlayers.topScorers.slice(0, rowCount),
                topAssists: widgetData.topPlayers.topAssists.slice(0, rowCount),
                topGoalkeepers: widgetData.topPlayers.topGoalkeepers.slice(0, rowCount)
            })
            setPlayerLoading(false)
            return
        }

        setPlayerLoading(true)
        try {
            const baseFilters: any = {
                org_id: orgId, // ITopRankFilter uses org_id
                page: 1,
                page_size: rowCount,
                order: 'desc',
                stat_level: 'total' // Default to total
            }

            // Set entity_id and stat_level based on player table filters
            if (selectedPlayerSeason && selectedPlayerSeason !== 'all') {
                if (selectedPlayerCompetition && selectedPlayerCompetition !== 'all') {
                    baseFilters.entity_id = selectedPlayerSeason
                    baseFilters.stat_level = 'season'
                } else {
                    baseFilters.entity_id = selectedPlayerSeason
                    baseFilters.stat_level = 'season'
                }
            } else if (selectedPlayerCompetition && selectedPlayerCompetition !== 'all') {
                baseFilters.entity_id = selectedPlayerCompetition
                baseFilters.stat_level = 'competition'
            } else if (playerFederation) {
                baseFilters.entity_id = playerFederation
                baseFilters.stat_level = 'federation'
            }

            // Add region filter for Europe only (if not filtered by competition/season)
            if (!baseFilters.entity_id && !baseFilters.stat_level) {
                baseFilters.region = 'Europe'
            }

            const [scorers, assists, goalkeepers] = await Promise.all([
                persistentQuery(
                    `standing:players:goals:${JSON.stringify(baseFilters)}`,
                    () => fetchOrgTopPlayers({ ...baseFilters, sort_by: 'goals_scored' }),
                    2 * 60 * 1000
                ),
                persistentQuery(
                    `standing:players:assists:${JSON.stringify(baseFilters)}`,
                    () => fetchOrgTopPlayers({ ...baseFilters, sort_by: 'assists' }),
                    2 * 60 * 1000
                ),
                persistentQuery(
                    `standing:players:goalkeepers:${JSON.stringify(baseFilters)}`,
                    () =>
                        fetchOrgTopPlayers({
                            ...baseFilters,
                            position: 'Goalkeeper',
                            sort_by: 'average_rating'
                        }),
                    2 * 60 * 1000
                )
            ])

            setPlayerData({
                topScorers: scorers?.slice(0, rowCount) || [],
                topAssists: assists?.slice(0, rowCount) || [],
                topGoalkeepers: goalkeepers?.slice(0, rowCount) || []
            })
        } catch (error) {
            console.error('Error fetching player data:', error)
        } finally {
            setPlayerLoading(false)
        }
    }, [
        orgId,
        rowCount,
        data?.playerFederationId,
        selectedPlayerCompetition,
        selectedPlayerSeason,
        widgetData?.topPlayers
    ])

    // Only show tabs if both tables are enabled
    const showTabs = showTeamTable && showPlayerTable

    // Fetch data when filters change
    useEffect(() => {
        if (!shouldLoad) return
        if (showTeamTable && (showTabs ? activeButton === 'first' : true)) {
            fetchTeamData()
        }
        if (showPlayerTable && (showTabs ? activeButton === 'second' : true)) {
            fetchPlayerData()
        }
    }, [
        shouldLoad,
        activeButton,
        selectedCompetition,
        selectedSeason,
        selectedPlayerCompetition,
        selectedPlayerSeason,
        orgId,
        showTeamTable,
        showPlayerTable,
        showTabs,
        fetchTeamData,
        fetchPlayerData
    ])

    const playerStatsSkeleton = (
        <div className='grid gap-y-4' role='status' aria-label='Loading player statistics'>
            {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className='flex items-center justify-between px-4 md:px-6 pb-4 last:pb-0'>
                    <div className='flex items-center gap-x-3 md:gap-x-4'>
                        <Skeleton className='size-[45px] rounded-sm md:size-[55px]' />
                        <div className='space-y-2'>
                            <Skeleton className='h-4 w-28 md:w-36' />
                            <Skeleton className='h-3 w-20 opacity-70 md:w-24' />
                        </div>
                    </div>
                    <Skeleton className='h-5 w-7' />
                </div>
            ))}
            <span className='sr-only'>Loading player statistics</span>
        </div>
    )

    const reusablePlayerComp = (players: ITopPlayerStats[], statValue: string) => (
        <div className='grid gap-y-4'>
            {players.length > 0 ? (
                players.slice(0, Math.min(rowCount, players.length)).map((player, index) => (
                    <div
                        key={player.player_id}
                        className={cn(
                            'flex items-center justify-between w-full px-4 md:px-6',
                            index !== players.length - 1 && index < rowCount - 1
                                ? 'border-b border-accent-foreground/30 pb-4'
                                : ''
                        )}
                    >
                        <div className='flex gap-x-3 md:gap-x-4'>
                            <div className='relative grid p-1 gap-4 size-fit bg-accent-foreground [clip-path:polygon(14%_0,100%_0,100%_88%,88%_100%,0_100%,0_12%)]'>
                                <div className='[clip-path:polygon(12%_0,100%_0,100%_88%,88%_100%,0_100%,0_12%)] size-full flex flex-col'>
                                    <div className='relative size-[45px] md:size-[55px]'>
                                        <Image
                                            src={player.player_picture || '/images/pfl-testing/playing_game.png'}
                                            alt={player.player_name}
                                            fill
                                            className='object-cover'
                                            sizes='(max-width: 768px) 45px, 55px'
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className='flex items-center flex-col gap-y-2'>
                                <div className='flex'>
                                    <p className='text-sm md:text-base'>{player.player_name}</p>
                                </div>
                            </div>
                        </div>
                        <p className='font-medium text-base md:text-lg'>
                            {statValue === 'goals'
                                ? player.goals_scored
                                : statValue === 'assists'
                                  ? player.assists
                                  : player.average_rating?.toFixed(1)}
                        </p>
                    </div>
                ))
            ) : (
                <p className='text-center text-muted-foreground py-4'>No players data available</p>
            )}
        </div>
    )

    const reusableTeamComp = (
        <Table className='text-xs md:text-sm w-full'>
            <TableHeader>
                <TableRow>
                    <TableHead className='w-12'></TableHead>
                    <TableHead>Team</TableHead>
                    <TableHead className='text-center'>GP</TableHead>
                    <TableHead className='text-center'>PTS</TableHead>
                    <TableHead className='text-center'>W</TableHead>
                    <TableHead className='text-center'>D</TableHead>
                    <TableHead className='text-center'>L</TableHead>
                    <TableHead className='text-center'>GF</TableHead>
                    <TableHead className='text-center'>GA</TableHead>
                    <TableHead className='text-center'>DIFF</TableHead>
                    <TableHead className='text-center'>LAST FIVE</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {teamLoading ? (
                    Array.from({ length: Math.min(rowCount, 5) }).map((_, index) => (
                        <TableRow key={`team-skeleton-${index}`} aria-label='Loading team statistics'>
                            <TableCell>
                                <Skeleton className='mx-auto h-4 w-4' />
                            </TableCell>
                            <TableCell>
                                <div className='flex items-center gap-3'>
                                    <Skeleton className='size-9 rounded-sm md:size-11' />
                                    <Skeleton className='h-4 w-24 md:w-32' />
                                </div>
                            </TableCell>
                            {Array.from({ length: 9 }).map((__, cellIndex) => (
                                <TableCell key={cellIndex}>
                                    <Skeleton className='mx-auto h-4 w-6' />
                                </TableCell>
                            ))}
                        </TableRow>
                    ))
                ) : teamData?.length > 0 ? (
                    teamData?.map((team, index) => (
                        <TableRow
                            key={team.team_id}
                            className='border-b border-accent-foreground/30 text-foreground text-sm md:text-base'
                        >
                            <TableCell className='font-medium text-center'>{index + 1}</TableCell>
                            <TableCell>
                                <div className='flex items-center gap-x-3 md:gap-x-4'>
                                    <div className='relative grid p-1 gap-4 size-fit bg-accent-foreground [clip-path:polygon(14%_0,100%_0,100%_88%,88%_100%,0_100%,0_12%)]'>
                                        <div className='[clip-path:polygon(12%_0,100%_0,100%_88%,88%_100%,0_100%,0_12%)] size-full flex flex-col'>
                                            <div className='relative size-[36px] md:size-[45px]'>
                                                <Image
                                                    src={team.team_logo || '/images/pfl-testing/playing_game.png'}
                                                    alt={team.team_name}
                                                    fill
                                                    className='object-cover'
                                                    sizes='(max-width: 768px) 36px, 45px'
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className='flex flex-col gap-y-1'>
                                        <p className='font-medium text-sm md:text-base'>{team.team_name}</p>
                                    </div>
                                </div>
                            </TableCell>
                            <TableCell className='text-center font-medium'>{team.played_games}</TableCell>
                            <TableCell className='text-center font-medium'>{team.points}</TableCell>
                            <TableCell className='text-center font-medium'>{team.wins}</TableCell>
                            <TableCell className='text-center font-medium'>{team.draws}</TableCell>
                            <TableCell className='text-center font-medium'>{team.losses}</TableCell>
                            <TableCell className='text-center font-medium'>{team.goals_for}</TableCell>
                            <TableCell className='text-center font-medium'>{team.goals_against}</TableCell>
                            <TableCell className='text-center font-medium'>{team.goals_diff}</TableCell>
                            <TableCell className='text-center'>
                                <div className='flex gap-1 justify-center'>
                                    {(team.last_five_results || []).map((result, idx) => (
                                        <span
                                            key={idx}
                                            className={`text-xs font-bold uppercase ${
                                                result === 'W'
                                                    ? 'text-green-500'
                                                    : result === 'D'
                                                      ? 'text-yellow-500'
                                                      : 'text-red-500'
                                            }`}
                                        >
                                            {result}
                                        </span>
                                    ))}
                                </div>
                            </TableCell>
                        </TableRow>
                    ))
                ) : (
                    <TableRow>
                        <TableCell colSpan={11} className='text-center py-8 text-muted-foreground'>
                            No team data available
                        </TableCell>
                    </TableRow>
                )}
            </TableBody>
        </Table>
    )

    const filterSelect = (
        label: string,
        options: { id: string; name: string }[],
        value: string | undefined,
        onChange: (val: string) => void,
        isOpen: boolean,
        setIsOpen: (val: boolean) => void,
        disabled?: boolean,
        isLoading?: boolean
    ) => (
        <div className='relative py-2 font-medium w-full px-3 h-[42px] max-w-[180px] md:h-[48px] md:max-w-[200px]'>
            <Select
                value={value || 'all'}
                onValueChange={onChange}
                open={isOpen}
                onOpenChange={setIsOpen}
                disabled={disabled || isLoading}
            >
                <SelectTrigger
                    className='w-full h-full bg-transparent border-none p-0 focus:ring-0 focus:ring-offset-0'
                    selectIcon={
                        <IoIosArrowDown
                            className={`h-4 w-4 opacity-70 text-foreground transition-transform duration-300 ${isOpen ? 'rotate-180' : 'rotate-0'}`}
                        />
                    }
                >
                    <SelectValue>
                        <span className='font-bold whitespace-nowrap relative z-10 text-foreground flex items-center gap-2 text-xs md:text-sm'>
                            {isLoading
                                ? 'Loading...'
                                : value && value !== 'all'
                                  ? options.find(o => o.id === value)?.name
                                  : `All ${label}`}
                        </span>
                    </SelectValue>
                </SelectTrigger>
                <SelectContent className='border border-border [clip-path:polygon(6px_0,100%_0,100%_88%,88%_100%,0_100%,0_12%)] glassmorphism max-h-[300px] overflow-y-auto'>
                    <SelectItem value='all' className='cursor-pointer'>
                        All {label}
                    </SelectItem>
                    {options.map(option => (
                        <SelectItem key={option.id} value={option.id} className='cursor-pointer'>
                            {option.name}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>

            <svg
                className='absolute inset-0 w-full h-full -z-10 pointer-events-none'
                viewBox='-1 -1 102 42'
                preserveAspectRatio='none'
                xmlns='http://www.w3.org/2000/svg'
                aria-hidden='true'
            >
                <polygon
                    points='4,0 100,0 100,31 96,40 0,40 0,9'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='1'
                    strokeLinejoin='bevel'
                    strokeLinecap='butt'
                    vectorEffect='non-scaling-stroke'
                />
            </svg>
        </div>
    )

    // Searchable filter select for competitions
    const searchableFilterSelect = (
        label: string,
        options: { id: string; name: string }[],
        value: string | undefined,
        onChange: (val: string) => void,
        isOpen: boolean,
        setIsOpen: (val: boolean) => void,
        searchQuery: string,
        setSearchQuery: (val: string) => void,
        disabled?: boolean,
        isLoading?: boolean
    ) => {
        const filteredOptions = options.filter(option => option.name.toLowerCase().includes(searchQuery.toLowerCase()))

        const handleValueChange = (val: string) => {
            onChange(val)
            setSearchQuery('') // Clear search when an item is selected
        }

        return (
            <div className='relative py-2 font-medium w-full px-3 h-[42px] max-w-[180px] md:h-[48px] md:max-w-[200px]'>
                <Select
                    value={value || 'all'}
                    onValueChange={handleValueChange}
                    open={isOpen}
                    onOpenChange={setIsOpen}
                    disabled={disabled || isLoading}
                >
                    <SelectTrigger
                        className='w-full h-full bg-transparent border-none p-0 focus:ring-0 focus:ring-offset-0'
                        selectIcon={
                            <IoIosArrowDown
                                className={`h-4 w-4 opacity-70 text-foreground transition-transform duration-300 ${isOpen ? 'rotate-180' : 'rotate-0'}`}
                            />
                        }
                    >
                        <SelectValue>
                            <span className='font-bold whitespace-nowrap relative z-10 text-foreground flex items-center gap-2 text-xs md:text-sm'>
                                {isLoading
                                    ? 'Loading...'
                                    : value && value !== 'all'
                                      ? options.find(o => o.id === value)?.name
                                      : `All ${label}`}
                            </span>
                        </SelectValue>
                    </SelectTrigger>
                    <SelectContent className='border border-border [clip-path:polygon(6px_0,100%_0,100%_88%,88%_100%,0_100%,0_12%)] glassmorphism max-h-[300px] overflow-y-auto'>
                        <div className='p-2 sticky top-0 bg-popover z-10 border-b border-border'>
                            <Input
                                placeholder='Search competitions...'
                                value={searchQuery}
                                onChange={e => {
                                    e.stopPropagation()
                                    setSearchQuery(e.target.value)
                                }}
                                onClick={e => e.stopPropagation()}
                                onFocus={e => e.stopPropagation()}
                                className='h-8 text-sm bg-background'
                                onKeyDown={e => {
                                    e.stopPropagation()
                                    if (e.key === 'Escape') {
                                        setIsOpen(false)
                                    }
                                }}
                                autoFocus
                            />
                        </div>
                        <SelectItem value='all' className='cursor-pointer'>
                            All {label}
                        </SelectItem>
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map(option => (
                                <SelectItem key={option.id} value={option.id} className='cursor-pointer'>
                                    {option.name}
                                </SelectItem>
                            ))
                        ) : (
                            <div className='px-2 py-4 text-sm text-muted-foreground text-center'>
                                No competitions found
                            </div>
                        )}
                    </SelectContent>
                </Select>

                <svg
                    className='absolute inset-0 w-full h-full -z-10 pointer-events-none'
                    viewBox='-1 -1 102 42'
                    preserveAspectRatio='none'
                    xmlns='http://www.w3.org/2000/svg'
                    aria-hidden='true'
                >
                    <polygon
                        points='4,0 100,0 100,31 96,40 0,40 0,9'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth='1'
                        strokeLinejoin='bevel'
                        strokeLinecap='butt'
                        vectorEffect='non-scaling-stroke'
                    />
                </svg>
            </div>
        )
    }

    const reusableButton = (name: string) => (
        <button className='relative py-2 font-medium bg-transparent w-full px-4 md:px-6 h-[48px] md:h-[52px]'>
            <span className='font-bold whitespace-nowrap relative z-10 text-foreground flex items-center gap-3 text-sm md:text-base'>
                {name}
            </span>

            <svg
                className='absolute inset-0 w-full h-full -z-10 pointer-events-none'
                viewBox='-1 -1 102 42'
                preserveAspectRatio='none'
                xmlns='http://www.w3.org/2000/svg'
                aria-hidden='true'
            >
                <polygon
                    points='4,0 100,0 100,31 96,40 0,40 0,9'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='1'
                    strokeLinejoin='bevel'
                    strokeLinecap='butt'
                    vectorEffect='non-scaling-stroke'
                />
            </svg>
        </button>
    )

    return (
        <div ref={rootRef}>
            <PolygonWidgetWrapper className='mt-10'>
                <div className='flex items-center flex-col md:flex-row gap-4 text-left justify-start md:justify-between'>
                    <p className='font-extrabold text-foreground leading-normal mr-auto text-xl md:text-2xl lg:text-3xl'>
                        Stats & Standings
                    </p>

                    {showTabs && (
                        <div className='relative px-3 py-1 font-medium bg-transparent h-[50px] md:h-[55px] mr-auto md:mr-0'>
                            <div className='flex items-center gap-3'>
                                <button
                                    onClick={() => setActiveButton('first')}
                                    className={cn(
                                        'font-bold whitespace-nowrap relative z-10 text-foreground flex items-center gap-3 md:px-6 px-3 py-2 transition-all duration-200 cursor-pointer text-sm md:text-base',
                                        activeButton === 'first'
                                            ? 'bg-accent-foreground/5 [clip-path:polygon(12%_0,100%_0,100%_88%,88%_100%,0_100%,0_12%)]'
                                            : 'bg-transparent'
                                    )}
                                >
                                    Team Tables
                                </button>

                                <button
                                    onClick={() => setActiveButton('second')}
                                    className={cn(
                                        'font-bold whitespace-nowrap relative z-10 text-foreground flex items-center gap-3 md:px-6 px-3 py-2.5 transition-all duration-200 cursor-pointer text-sm md:text-base',
                                        activeButton === 'second'
                                            ? 'bg-accent-foreground/5 [clip-path:polygon(12%_0,100%_0,100%_88%,88%_100%,0_100%,0_12%)]'
                                            : 'bg-transparent'
                                    )}
                                >
                                    Player Tables
                                </button>
                            </div>

                            <svg
                                className='absolute inset-0 w-full h-full -z-10 pointer-events-none'
                                viewBox='-1 -1 102 42'
                                preserveAspectRatio='none'
                                xmlns='http://www.w3.org/2000/svg'
                                aria-hidden='true'
                            >
                                <polygon
                                    points='5,0 100,0 100,32 94,40 0,40 0,9'
                                    fill='currentColor'
                                    fillOpacity='0.06'
                                />
                                <polygon
                                    points='5,0 100,0 100,32 94,40 0,40 0,9'
                                    fill='none'
                                    stroke='currentColor'
                                    strokeWidth='1'
                                    strokeLinejoin='bevel'
                                    strokeLinecap='butt'
                                    vectorEffect='non-scaling-stroke'
                                />
                            </svg>
                        </div>
                    )}
                </div>

                <hr className='border-accent-foreground/50 mt-4 flex md:hidden' />

                {showTeamTable && (showTabs ? activeButton === 'first' : true) && (
                    <div className='grid gap-3 md:gap-4'>
                        <div className='flex flex-col gap-2 mt-3 md:mt-4'>
                            <p className='text-sm font-semibold text-muted-foreground'>Team table</p>
                            <div className='flex flex-col md:flex-row items-start gap-2 md:gap-3'>
                                {(competitions.length > 0 || competitionsLoading) &&
                                    searchableFilterSelect(
                                        'Competition',
                                        competitions.map(c => ({ id: c.id, name: c.name })),
                                        selectedCompetition,
                                        handleCompetitionChange,
                                        isCompetitionSelectOpen,
                                        (val: boolean) => {
                                            setIsCompetitionSelectOpen(val)
                                            if (!val) {
                                                setCompetitionSearchQuery('')
                                            }
                                        },
                                        competitionSearchQuery,
                                        setCompetitionSearchQuery,
                                        !enableFilters,
                                        competitionsLoading
                                    )}
                                {(seasons.length > 0 || seasonsLoading) &&
                                    filterSelect(
                                        'Season',
                                        seasons.map(s => ({ id: s.id, name: Seasons_getDescription(s) || s.id })),
                                        selectedSeason,
                                        setSelectedSeason,
                                        isSeasonSelectOpen,
                                        setIsSeasonSelectOpen,
                                        !enableFilters,
                                        seasonsLoading
                                    )}
                            </div>
                        </div>

                        <div className='grid border-b md:border-b-0 pb-4 md:pb-0 border-accent-foreground/30 gap-y-3 md:gap-y-4 md:pr-4'>
                            {reusableTeamComp}
                        </div>

                        {selectedCompetition && selectedCompetition !== 'all' && (
                            <div className='flex justify-end mt-4 md:mt-6'>
                                <Link
                                    href={`/competition/${selectedCompetition}/table`}
                                    className='relative py-2.5 font-medium bg-transparent w-fit px-6 md:px-8 h-[48px] md:h-[52px] group transition-all duration-200 hover:scale-105'
                                >
                                    <span className='font-bold whitespace-nowrap relative z-10 text-foreground flex items-center gap-2 md:gap-3 text-sm md:text-base'>
                                        View All Standing
                                        <RiArrowRightUpFill className='size-4 md:size-5 transition-transform duration-200 group-hover:translate-x-1 group-hover:-translate-y-1' />
                                    </span>

                                    <svg
                                        className='absolute inset-0 w-full h-full -z-10 pointer-events-none'
                                        viewBox='-1 -1 102 42'
                                        preserveAspectRatio='none'
                                        xmlns='http://www.w3.org/2000/svg'
                                        aria-hidden='true'
                                    >
                                        <polygon
                                            points='4,0 100,0 100,31 96,40 0,40 0,9'
                                            fill='none'
                                            stroke='currentColor'
                                            strokeWidth='1'
                                            strokeLinejoin='bevel'
                                            strokeLinecap='butt'
                                            vectorEffect='non-scaling-stroke'
                                        />
                                    </svg>
                                </Link>
                            </div>
                        )}
                    </div>
                )}

                {showPlayerTable && (showTabs ? activeButton === 'second' : true) && (
                    <div className='grid gap-3 md:gap-4'>
                        <div className='flex flex-col gap-2 mt-3 md:mt-4'>
                            <p className='text-sm font-semibold text-muted-foreground'>Player table</p>
                            <div className='flex flex-col md:flex-row items-start gap-2 md:gap-3'>
                                {(competitions.length > 0 || competitionsLoading) &&
                                    searchableFilterSelect(
                                        'Competition',
                                        competitions.map(c => ({ id: c.id, name: c.name })),
                                        selectedPlayerCompetition,
                                        handlePlayerCompetitionChange,
                                        isPlayerCompetitionSelectOpen,
                                        (val: boolean) => {
                                            setIsPlayerCompetitionSelectOpen(val)
                                            if (!val) {
                                                setPlayerCompetitionSearchQuery('')
                                            }
                                        },
                                        playerCompetitionSearchQuery,
                                        setPlayerCompetitionSearchQuery,
                                        !enableFilters,
                                        competitionsLoading
                                    )}
                                {(competitions.length > 0 || competitionsLoading) &&
                                    filterSelect(
                                        'Season',
                                        playerSeasons.map(s => ({
                                            id: s.id,
                                            name: Seasons_getDescription(s) || s.id
                                        })),
                                        selectedPlayerSeason,
                                        setSelectedPlayerSeason,
                                        isPlayerSeasonSelectOpen,
                                        setIsPlayerSeasonSelectOpen,
                                        !enableFilters ||
                                            !selectedPlayerCompetition ||
                                            selectedPlayerCompetition === 'all',
                                        playerSeasonsLoading
                                    )}
                            </div>
                        </div>

                        <div className='grid md:grid-cols-3 gap-3 md:gap-4'>
                            <div className='grid mt-4 md:border-r border-b md:border-b-0 pb-4 md:pb-0 border-accent-foreground/30 gap-y-3 md:gap-y-4 md:pr-4'>
                                {reusableButton('Top Scorer')}
                                {playerLoading
                                    ? playerStatsSkeleton
                                    : reusablePlayerComp(playerData.topScorers, 'goals')}
                            </div>

                            <div className='grid md:mt-4 md:border-r border-b md:border-b-0 pb-4 md:pb-0 border-accent-foreground/30 gap-y-3 md:gap-y-4 md:pr-4'>
                                {reusableButton('Top Assist')}
                                {playerLoading
                                    ? playerStatsSkeleton
                                    : reusablePlayerComp(playerData.topAssists, 'assists')}
                            </div>

                            <div className='grid md:mt-4 md:border-r last:md:border-r-0 border-b md:border-b-0 pb-4 md:pb-0 border-accent-foreground/30 gap-y-3 md:gap-y-4 md:pr-4'>
                                {reusableButton('Top Goal Keeper')}
                                {playerLoading
                                    ? playerStatsSkeleton
                                    : reusablePlayerComp(playerData.topGoalkeepers, 'rating')}
                            </div>
                        </div>

                        {competitions.length > 0 &&
                            (selectedPlayerCompetition && selectedPlayerCompetition !== 'all'
                                ? true
                                : competitions[0]?.id) && (
                                <div className='flex justify-end mt-4 md:mt-6'>
                                    <Link
                                        href={`/competition/${
                                            selectedPlayerCompetition && selectedPlayerCompetition !== 'all'
                                                ? selectedPlayerCompetition
                                                : competitions[0]?.id
                                        }/stats${selectedPlayerSeason ? `?season_id=${selectedPlayerSeason}` : ''}`}
                                        className='relative py-2.5 font-medium bg-transparent w-fit px-6 md:px-8 h-[48px] md:h-[52px] group transition-all duration-200 hover:scale-105'
                                    >
                                        <span className='font-bold whitespace-nowrap relative z-10 text-foreground flex items-center gap-2 md:gap-3 text-sm md:text-base'>
                                            View all
                                            <RiArrowRightUpFill className='size-4 md:size-5 transition-transform duration-200 group-hover:translate-x-1 group-hover:-translate-y-1' />
                                        </span>

                                        <svg
                                            className='absolute inset-0 w-full h-full -z-10 pointer-events-none'
                                            viewBox='-1 -1 102 42'
                                            preserveAspectRatio='none'
                                            xmlns='http://www.w3.org/2000/svg'
                                            aria-hidden='true'
                                        >
                                            <polygon
                                                points='4,0 100,0 100,31 96,40 0,40 0,9'
                                                fill='none'
                                                stroke='currentColor'
                                                strokeWidth='1'
                                                strokeLinejoin='bevel'
                                                strokeLinecap='butt'
                                                vectorEffect='non-scaling-stroke'
                                            />
                                        </svg>
                                    </Link>
                                </div>
                            )}
                    </div>
                )}
            </PolygonWidgetWrapper>
        </div>
    )
}

export default StandingStatWidget
